import React, { useState, useMemo } from "react";
import { Card, Row, Col, Select, Table, Typography, Tag, Tooltip } from "antd";
import { FileOutlined, UserOutlined, ApartmentOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { extractFilesList, getProjects, getMonthsByProject, getRegistroNombre } from "./file-utils";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import ExportarGestionArchivos from "./ExportarGestionArchivos"; // Ajusta la ruta

const { Title, Text } = Typography;

const REGISTRO_TAGS = {
  participante: { color: "#009728", icon: <UserOutlined />, label: "Participante" },
  organizacion: { color: "#1976D2", icon: <ApartmentOutlined />, label: "Organización" },
  sin_registro: { color: "#FAAD14", icon: <QuestionCircleOutlined />, label: "Sin registro" },
};

export function FileManagementDashboard({ data }) {
  const [selectedProject, setSelectedProject] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [registroFilter, setRegistroFilter] = useState("all");

  const allFiles = useMemo(() => extractFilesList(data), [data]);
  const projects = useMemo(() => getProjects(data), [data]);
  const months = useMemo(() => {
    if (selectedProject === "all") return [];
    return getMonthsByProject(data, selectedProject);
  }, [data, selectedProject]);

  const filteredFiles = useMemo(() => {
    return allFiles.filter((file) => {
      if (selectedProject !== "all" && file.project !== selectedProject)
        return false;
      if (selectedMonth !== "all" && file.month !== selectedMonth)
        return false;
      if (registroFilter !== "all" && file.tipoRegistro !== registroFilter) return false;
      return true;
    });
  }, [allFiles, selectedProject, selectedMonth, registroFilter]);

  const stats = useMemo(() => {
    const totalFiles = filteredFiles.length;
    const filesWithParticipante = filteredFiles.filter((f) => f.tipoRegistro === "participante").length;
    const filesWithOrganizacion = filteredFiles.filter((f) => f.tipoRegistro === "organizacion").length;
    const filesSinRegistro = totalFiles - filesWithParticipante - filesWithOrganizacion;

    return { totalFiles, filesWithParticipante, filesWithOrganizacion, filesSinRegistro };
  }, [filteredFiles]);

  function cleanFileName(name) {
    return name.replace(/^[0-9]+-/, "");
  }

  function renderEllipsisText(text) {
    if (!text) return "—";
    return (
      <Tooltip title={text} placement="topLeft">
        <span
          style={{
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {text}
        </span>
      </Tooltip>
    );
  }

  const columns = [
    {
      title: "Archivo",
      dataIndex: "name",
      key: "name",
      width: 280,
      render: (text) => renderEllipsisText(cleanFileName(text)),
    },
    {
      title: "Proyecto",
      dataIndex: "project",
      key: "project",
      width: 180,
      render: (text) => renderEllipsisText(text),
    },
    {
      title: "Mes",
      dataIndex: "month",
      key: "month",
      width: 150,
    },
    {
      title: "Tipo",
      key: "tipoRegistro",
      align: "center",
      width: 100,
      render: (_, record) => {
        const info = REGISTRO_TAGS[record.tipoRegistro] || REGISTRO_TAGS.sin_registro;
        return (
          <Tag color={info.color} style={{ color: "white" }} icon={info.icon}>
            {info.label}
          </Tag>
        );
      },
    },
    {
      title: "Registrado a",
      key: "registrado",
      width: 220,
      render: (_, record) => renderEllipsisText(getRegistroNombre(record)),
    },
    {
      title: "Identificador",
      key: "identificador",
      width: 100,
      render: (_, record) => {
        if (record.tipoRegistro === "organizacion" && record.organizacion) {
          return record.organizacion.Nit || record.organizacion.sigla || "—";
        }
        return record.cedula || "—";
      },
    },
  ];

  const statCards = [
    { key: "totalFiles", label: "Total de archivos", value: stats.totalFiles, icon: <FileOutlined style={{ color: "#338BC7", fontSize: 32 }} /> },
    { key: "filesWithParticipante", label: "Con participante", value: stats.filesWithParticipante, icon: <UserOutlined style={{ color: "#009728", fontSize: 32 }} /> },
    { key: "filesWithOrganizacion", label: "Con organización", value: stats.filesWithOrganizacion, icon: <ApartmentOutlined style={{ color: "#1976D2", fontSize: 32 }} /> },
    { key: "filesSinRegistro", label: "Sin registro", value: stats.filesSinRegistro, icon: <QuestionCircleOutlined style={{ color: "#FAAD14", fontSize: 32 }} /> },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, overflowX: "hidden" }}>
      <Row gutter={[16, 16]}>
        {statCards.map((card) => (
          <Col xs={24} sm={12} md={6} key={card.key}>
            <Card
              hoverable
              style={{
                height: "140px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: "16px",
                borderRadius: "8px",
              }}
            >
              <Row align="middle" justify="center" style={{ height: "100%" }}>
                <Col style={{ textAlign: "center" }}>
                  <Text strong>{card.label}</Text>
                  <div style={{ height: 8, margin: "0 auto" }}>
                    <div
                      style={{
                        width: 20,
                        height: 4,
                        backgroundColor: "#3bbc37ff",
                        borderRadius: 2,
                        margin: "0 auto",
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                      fontSize: 28,
                      fontWeight: "bold",
                      color: "#338BC7",
                      marginTop: 12,
                    }}
                  >
                    {card.icon}
                    <span>{card.value}</span>
                  </div>
                </Col>
              </Row>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        style={{
          padding: "10px",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          backgroundColor: "#042956",
          marginBottom: "24px",
          border: "none",
          width: "100%"
        }}
      >
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          flexWrap: "wrap",
          gap: "16px"
        }}>
          <Title level={2} style={{
            margin: 0,
            color: "#ffffff",
            fontSize: "20px",
            fontWeight: 600
          }}>
            <FontAwesomeIcon icon={faMagnifyingGlass} style={{ marginRight: "12px", opacity: 0.9 }} />
            Filtro de búsqueda
          </Title>


        </div>

        <div style={{
          display: "flex",
          gap: "16px",
          flexWrap: "wrap",
          alignItems: "center"
        }}>
          <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
            <Select
              value={selectedProject}
              onChange={(value) => {
                setSelectedProject(value);
                setSelectedMonth("all");
              }}
              style={{ width: "100%", marginTop: 8 }}
              options={[
                { label: "Todos", value: "all" },
                ...projects.map((p) => ({ label: p, value: p })),
              ]}
            />
          </div>

          <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
            <Select
              disabled={selectedProject === "all"}
              value={selectedMonth}
              onChange={(value) => setSelectedMonth(value)}
              style={{ width: "100%", marginTop: 8, backgroundColor: "#c1c1c1ff", borderRadius: 4 }}
              options={[
                { label: "Todos", value: "all" },
                ...months.map((m) => ({ label: m, value: m })),
              ]}
            />
          </div>

          <div style={{ flex: "1 1 200px", minWidth: "200px" }}>
            <Select
              value={registroFilter}
              onChange={(value) => setRegistroFilter(value)}
              style={{ width: "100%", marginTop: 8 }}
              options={[
                { label: "Todos los registros", value: "all" },
                { label: "Participante", value: "participante" },
                { label: "Organización", value: "organizacion" },
                { label: "Sin registro", value: "sin_registro" },
              ]}
            />
          </div>
          <ExportarGestionArchivos
            filteredFiles={filteredFiles}
            stats={stats}
            selectedProject={selectedProject}
            selectedMonth={selectedMonth}
            registroFilter={registroFilter}
            projects={projects}
            months={months}
          />
        </div>
      </Card>

      <Table
        columns={columns}
        dataSource={filteredFiles}
        rowKey="path"
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} de ${total} archivos`
        }}
        scroll={{
          x: 1600,
          y: 600
        }}
        size="middle"
        sticky={true}
        style={{
          fontSize: '14px'
        }}
      />
    </div>
  );
}
